import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowLeft, Quote, Search, Lightbulb, Palette, Rocket, BarChart3, Mail } from 'lucide-react';

// Mask Reveal Animation Component - Clean scroll-based reveal
const MaskReveal = ({ children, className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "start 0.3"]
  });
  
  const clipPath = useTransform(
    scrollYProgress,
    [0, 1],
    ["inset(100% 0% 0% 0%)", "inset(0% 0% 0% 0%)"]
  );
  
  const opacity = useTransform(scrollYProgress, [0, 0.5], [0, 1]);

  return (
    <motion.div
      ref={ref}
      style={{ clipPath, opacity }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Simple Reveal on Scroll
const RevealOnScroll = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============== DATA ==============

const testimonials = [
  {
    quote: "Team Zenith translated our hospitality philosophy into a cohesive digital narrative. From brand articulation to execution, their strategic clarity and aesthetic precision elevated The Bagheera's positioning across platforms.",
    name: "Akshay Singhi",
    company: "The Bagheera"
  },
  {
    quote: "Zenith understood our product depth and premium positioning. Their communication framework highlighted craftsmanship, exclusivity, and design differentiation, resulting in stronger brand recall.",
    name: "Vikalp Gupta",
    company: "Golden Locks"
  },
  {
    quote: "Zenith Marketing brought structural clarity to our brand and digital ecosystem. From positioning to website architecture, their execution was systematic and data-driven.",
    name: "CA Aneesh Patni",
    company: "TOF Consultancy"
  },
  {
    quote: "Zenith Marketing approached our mandate with strategic depth and execution discipline. Their brand structuring & communication clarity marked a stronger market perception.",
    name: "Ali Akbar Khan",
    company: "Salasar Group"
  },
  {
    quote: "Over six years, Zenith has handled our brand with consistency and operational clarity. Their understanding of product cycles has strengthened our positioning.",
    name: "Abhimanyu Singhi",
    company: "Arham Knitwears"
  },
  {
    quote: "Zenith approaches growth with structured experimentation and disciplined optimisation. Their understanding of audience segmentation has consistently improved efficiency.",
    name: "Arushi Lohani",
    company: "FindYourFit"
  }
];

const blogs = [
  {
    slug: 'ai-and-future-of-marketing',
    title: 'AI IS NOT REPLACEMENT. IT IS AMPLIFICATION.',
    excerpt: 'Strip away the headline drama, and AI is moving straight into territory once considered automation-proof.',
    image: '/blog-ai-3d.png',
    date: 'December 2024'
  },
  {
    slug: 'social-media-metrics-2026',
    title: 'THE "LIKE" BUTTON IS OFFICIALLY A VANITY METRIC',
    excerpt: 'If you\'re still reporting reach and likes in 2026, you\'re measuring the past.',
    image: '/blog-social-cover.jpg',
    date: 'November 2024'
  }
];

const featuredCaseStudies = [
  { slug: 'findyourfit', client: 'FINDYOURFIT', tagline: 'Wellness Platform', image: '/find-your-fit-event.jpg' },
  { slug: 'goat', client: 'GOAT SILIGURI', tagline: 'Premium Dining', image: '/goat-new.jpg' },
  { slug: 'bagheera', client: 'THE BAGHEERA', tagline: 'Luxury Hospitality', image: '/bagheera-heritage-guests.jpg' }
];

const services = [
  { img: '/3d-brand-strategy.png', title: 'BRAND STRATEGY', desc: 'Positioning, voice & communication frameworks' },
  { img: '/3d-social-media-mgmt.png', title: 'SOCIAL MEDIA', desc: 'Content planning & audience engagement' },
  { img: '/3d-performance-marketing.png', title: 'PERFORMANCE ADS', desc: 'Meta, Google & programmatic campaigns' },
  { img: '/3d-content-production.png', title: 'CONTENT PRODUCTION', desc: 'Photography, video & creative design' },
  { img: '/3d-website-design.png', title: 'WEBSITE DEVELOPMENT', desc: 'Modern, conversion-focused web experiences' },
  { img: '/3d-creative-design.png', title: 'CREATIVE DESIGN', desc: 'Visual identity & campaign creatives' }
];

const roadmapSteps = [
  { 
    num: '01',
    icon: Search, 
    title: 'DISCOVERY', 
    desc: 'Understanding your brand, market, audience and objectives through research.'
  },
  { 
    num: '02',
    icon: Lightbulb, 
    title: 'STRATEGY', 
    desc: 'Developing tailored marketing frameworks aligned with your business goals.'
  },
  { 
    num: '03',
    icon: Palette, 
    title: 'CREATIVE', 
    desc: 'Crafting compelling designs and messaging that represents your brand.'
  },
  { 
    num: '04',
    icon: Rocket, 
    title: 'EXECUTION', 
    desc: 'Launching and managing campaigns with precision across all channels.'
  },
  { 
    num: '05',
    icon: BarChart3, 
    title: 'OPTIMISE', 
    desc: 'Continuous monitoring and iteration to maximize effectiveness.'
  }
];

// ============== TESTIMONIALS CAROUSEL ==============

const TestimonialsCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const testimonialsPerView = 2;
  const totalSlides = Math.ceil(testimonials.length / testimonialsPerView);
  
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);
  
  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };
  
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(goToNext, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, goToNext]);

  const getVisibleTestimonials = () => {
    const startIdx = currentIndex * testimonialsPerView;
    return testimonials.slice(startIdx, startIdx + testimonialsPerView);
  };

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      <div className="flex justify-end gap-3 mb-8">
        <button 
          onClick={goToPrev}
          className="w-12 h-12 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center hover:bg-gradient-to-r hover:from-brand-blue hover:to-cyan-400 hover:border-transparent text-white transition-all duration-300"
        >
          <ArrowLeft size={20} />
        </button>
        <button 
          onClick={goToNext}
          className="w-12 h-12 rounded-full bg-gradient-to-r from-brand-blue to-cyan-400 text-white flex items-center justify-center hover:from-cyan-400 hover:to-brand-blue transition-all duration-300"
        >
          <ArrowRight size={20} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AnimatePresence mode="wait">
          {getVisibleTestimonials().map((testimonial, idx) => (
            <motion.div
              key={`${currentIndex}-${idx}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 md:p-10 border border-white/10 hover:border-white/20 transition-all duration-300"
            >
              <Quote className="text-brand-blue w-10 h-10 mb-6 opacity-60" />
              <p className="text-white/80 text-lg leading-relaxed mb-8">
                "{testimonial.quote}"
              </p>
              <div className="flex items-center">
                <div className="w-14 h-14 bg-gradient-to-br from-brand-blue to-cyan-400 rounded-full flex items-center justify-center text-white font-semibold text-xl">
                  {testimonial.name.charAt(0)}
                </div>
                <div className="ml-4">
                  <p className="font-semibold text-white">{testimonial.name}</p>
                  <p className="text-brand-blue text-sm font-medium">{testimonial.company}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <div className="flex justify-center gap-2 mt-8">
        {Array.from({ length: totalSlides }).map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            className={`h-2 rounded-full transition-all duration-300 ${
              idx === currentIndex ? 'bg-gradient-to-r from-brand-blue to-cyan-400 w-8' : 'bg-white/30 w-2 hover:bg-white/50'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// ============== ROADMAP HOW WE WORK SECTION ==============

const RoadmapSection = () => {
  return (
    <div className="relative">
      {/* Desktop Horizontal Roadmap */}
      <div className="hidden lg:block">
        {/* Connecting Line */}
        <div className="absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-blue/30 to-transparent" />
        
        <div className="grid grid-cols-5 gap-4">
          {roadmapSteps.map((step, idx) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15, duration: 0.5 }}
                className="relative text-center"
              >
                {/* Step Number Badge */}
                <div className="relative z-10 mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-brand-blue to-cyan-400 flex items-center justify-center shadow-lg shadow-brand-blue/30">
                  <Icon className="w-8 h-8 text-white" />
                </div>
                
                {/* Connector Dot */}
                <div className="absolute top-24 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-gradient-to-br from-brand-blue to-cyan-400 border-4 border-gray-900" />
                
                {/* Content */}
                <div className="mt-12 pt-4">
                  <span className="text-brand-blue text-sm font-bold tracking-wider">STEP {step.num}</span>
                  <h3 className="font-outfit text-xl font-bold text-white mt-2 mb-3 uppercase">{step.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Mobile/Tablet Vertical Roadmap */}
      <div className="lg:hidden relative">
        {/* Vertical Line */}
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-brand-blue via-cyan-400 to-brand-blue/30" />
        
        <div className="space-y-12">
          {roadmapSteps.map((step, idx) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
                className="relative flex items-start gap-6 pl-4"
              >
                {/* Icon Circle */}
                <div className="relative z-10 flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-brand-blue to-cyan-400 flex items-center justify-center shadow-lg shadow-brand-blue/30">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                
                {/* Content */}
                <div className="flex-1 pt-2">
                  <span className="text-brand-blue text-xs font-bold tracking-wider">STEP {step.num}</span>
                  <h3 className="font-outfit text-xl font-bold text-white mt-1 mb-2 uppercase">{step.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============== MAIN COMPONENT ==============

const HomePage = () => {
  const [email, setEmail] = useState('');
  
  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    window.location.href = `mailto:info@zenithmarketingindia.com?subject=Newsletter Subscription&body=Please add me to your newsletter. Email: ${email}`;
  };

  return (
    <div className="w-full">
      
      {/* ============== HERO SECTION ============== */}
      <section className="min-h-screen flex items-center relative overflow-hidden pt-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-testid="hero-section">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-20 right-10 w-[600px] h-[600px] bg-gradient-to-bl from-brand-blue/20 via-cyan-500/10 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-[400px] h-[400px] bg-gradient-to-tr from-purple-500/10 via-brand-blue/10 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="text-left">
              <RevealOnScroll delay={0.1}>
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-brand-blue/20 to-cyan-400/20 border border-brand-blue/30 text-brand-blue text-sm font-semibold mb-8 uppercase tracking-wider">
                  <span className="w-2 h-2 bg-brand-blue rounded-full mr-3 animate-pulse" />
                  DIGITAL MARKETING AGENCY
                </span>
              </RevealOnScroll>

              <RevealOnScroll delay={0.2}>
                <h1 className="font-outfit text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] mb-8 uppercase">
                  <span className="text-white">WE BUILD</span>
                  <br />
                  <span className="bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent">BRANDS THAT</span>
                  <br />
                  <span className="text-white">STAND OUT</span>
                </h1>
              </RevealOnScroll>

              <RevealOnScroll delay={0.3}>
                <p className="text-lg md:text-xl text-white/70 max-w-lg leading-relaxed mb-10">
                  A boutique agency specializing in Social Media, Branding & Digital Strategy for premium brands across India.
                </p>
              </RevealOnScroll>

              <RevealOnScroll delay={0.4}>
                <div className="flex flex-col sm:flex-row gap-4 mb-14">
                  <Link
                    to="/contact"
                    data-testid="hero-cta-btn"
                    className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-brand-blue to-cyan-400 text-white font-semibold rounded-full hover:from-cyan-400 hover:to-brand-blue transition-all duration-300 uppercase tracking-wide shadow-lg shadow-brand-blue/30"
                  >
                    START YOUR PROJECT
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <Link
                    to="/case-studies"
                    data-testid="hero-work-btn"
                    className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 uppercase tracking-wide"
                  >
                    VIEW OUR WORK
                  </Link>
                </div>
              </RevealOnScroll>

              <RevealOnScroll delay={0.5}>
                <div className="grid grid-cols-3 gap-8 pt-8 border-t border-white/10">
                  {[
                    { value: '50+', label: 'BRANDS SERVED' },
                    { value: '8+', label: 'YEARS EXPERIENCE' },
                    { value: '100+', label: 'PROJECTS DONE' }
                  ].map((stat, idx) => (
                    <div key={idx} className="text-left">
                      <div className="font-outfit text-3xl lg:text-4xl font-bold bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent">{stat.value}</div>
                      <div className="text-xs text-white/50 mt-1 tracking-wider">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </RevealOnScroll>
            </div>

            <RevealOnScroll delay={0.3} className="flex justify-center">
              <motion.img 
                src="/girl-analytics.png" 
                alt="Digital Marketing Services by Zenith Marketing India" 
                className="w-full max-w-lg drop-shadow-2xl"
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* ============== SERVICES SECTION - WHAT WE DO (LIGHT) ============== */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-gray-50 to-gray-100" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll>
            <div className="text-center mb-20">
              <span className="text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">WHAT WE DO</span>
              <h2 className="font-outfit text-4xl md:text-5xl font-bold text-gray-900 mb-4 uppercase">
                SERVICES THAT DRIVE <span className="bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent">GROWTH</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Comprehensive digital marketing solutions tailored for premium brands
              </p>
            </div>
          </RevealOnScroll>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <Link to="/services">
                  <motion.div 
                    className="group bg-white rounded-3xl p-8 h-full border border-gray-100 hover:border-brand-blue/30 transition-all duration-500 relative overflow-hidden"
                    whileHover={{ 
                      y: -8,
                      boxShadow: "0 25px 50px -12px rgba(0, 168, 232, 0.25)"
                    }}
                  >
                    {/* Gradient Background on Hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/5 to-cyan-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    {/* 3D Icon - Large and Highlighted */}
                    <motion.div 
                      className="relative z-10 mb-6"
                      whileHover={{ scale: 1.15, rotate: [0, -3, 3, 0] }}
                      transition={{ duration: 0.4 }}
                    >
                      <div className="w-48 h-48 mx-auto relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/30 to-cyan-400/30 rounded-full blur-3xl scale-150 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <img 
                          src={service.img} 
                          alt={service.title}
                          className="w-full h-full object-contain relative z-10 drop-shadow-xl group-hover:drop-shadow-2xl transition-all duration-500"
                        />
                      </div>
                    </motion.div>
                    
                    <h3 className="relative z-10 font-outfit text-xl font-bold text-gray-900 text-center mb-3 group-hover:bg-gradient-to-r group-hover:from-brand-blue group-hover:to-cyan-400 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300 tracking-wide uppercase">
                      {service.title}
                    </h3>
                    <p className="relative z-10 text-gray-500 text-center">{service.desc}</p>
                    
                    {/* Arrow indicator */}
                    <div className="relative z-10 flex justify-center mt-6 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                      <span className="text-brand-blue font-semibold text-sm uppercase tracking-wide flex items-center">
                        LEARN MORE <ArrowRight className="ml-2 w-4 h-4" />
                      </span>
                    </div>
                  </motion.div>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center mt-16"
          >
            <Link
              to="/services"
              className="inline-flex items-center px-10 py-5 border-2 border-brand-blue text-brand-blue font-bold rounded-full hover:bg-gradient-to-r hover:from-brand-blue hover:to-cyan-400 hover:text-white hover:border-transparent transition-all duration-300 uppercase tracking-wide text-lg"
            >
              EXPLORE ALL SERVICES
              <ArrowRight className="ml-3 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ============== HOW WE WORK - ROADMAP (DARK) ============== */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden" data-testid="approach-section">
        {/* Background Decorations */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-gradient-to-br from-brand-blue/10 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-gradient-to-tl from-cyan-500/10 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll>
            <div className="text-center mb-20">
              <span className="text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">OUR PROCESS</span>
              <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-4 uppercase">
                HOW WE <span className="bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent">WORK</span>
              </h2>
              <p className="text-lg text-white/60 max-w-2xl mx-auto">
                A structured roadmap to deliver consistent results
              </p>
            </div>
          </RevealOnScroll>

          <RevealOnScroll delay={0.2}>
            <RoadmapSection />
          </RevealOnScroll>
        </div>
      </section>

      {/* ============== FEATURED WORK SECTION (LIGHT) ============== */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-gray-50 to-gray-100" data-testid="work-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16">
              <div>
                <span className="text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">PORTFOLIO</span>
                <h2 className="font-outfit text-4xl md:text-5xl font-bold text-gray-900 uppercase">
                  SUCCESS <span className="bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent">STORIES</span>
                </h2>
              </div>
              <Link to="/case-studies" className="mt-6 md:mt-0 text-gray-700 font-medium flex items-center hover:text-brand-blue transition-colors uppercase tracking-wide">
                VIEW ALL WORK 
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </RevealOnScroll>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredCaseStudies.map((study, idx) => (
              <RevealOnScroll key={idx} delay={idx * 0.1}>
                <Link to={`/case-studies/${study.slug}`}>
                  <div className="group relative overflow-hidden rounded-2xl aspect-[4/3] shadow-lg">
                    <img 
                      src={study.image} 
                      alt={study.client}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="text-brand-blue text-sm font-semibold uppercase tracking-wider">{study.tagline}</span>
                      <h3 className="font-outfit text-2xl font-bold text-white mt-1 group-hover:bg-gradient-to-r group-hover:from-brand-blue group-hover:to-cyan-400 group-hover:bg-clip-text group-hover:text-transparent transition-all uppercase">
                        {study.client}
                      </h3>
                    </div>
                  </div>
                </Link>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* ============== TESTIMONIALS (DARK) ============== */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden" data-testid="testimonials-section">
        <div className="absolute inset-0 -z-10">
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-brand-blue/10 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll>
            <div className="mb-12">
              <span className="text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">TESTIMONIALS</span>
              <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white uppercase">
                WHAT CLIENTS <span className="bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent">SAY</span>
              </h2>
            </div>
          </RevealOnScroll>
          
          <RevealOnScroll delay={0.2}>
            <TestimonialsCarousel />
          </RevealOnScroll>
        </div>
      </section>

      {/* ============== BLOG SECTION (LIGHT) ============== */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-gray-50 to-gray-100" data-testid="blog-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16">
              <div>
                <span className="text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">INSIGHTS</span>
                <h2 className="font-outfit text-4xl md:text-5xl font-bold text-gray-900 uppercase">
                  FRESH <span className="bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent">PERSPECTIVES</span>
                </h2>
              </div>
              <Link to="/blog" className="mt-6 md:mt-0 text-brand-blue font-medium flex items-center hover:text-cyan-500 transition-colors uppercase tracking-wide">
                ALL ARTICLES 
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </RevealOnScroll>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {blogs.map((blog, idx) => (
              <RevealOnScroll key={idx} delay={idx * 0.1}>
                <Link to={`/blog/${blog.slug}`}>
                  <div className="group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300">
                    <div className="aspect-[16/9] overflow-hidden">
                      <img 
                        src={blog.image} 
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6 md:p-8">
                      <span className="text-sm text-brand-blue font-semibold uppercase tracking-wider">{blog.date}</span>
                      <h3 className="font-outfit text-lg font-bold text-gray-900 mt-2 mb-3 group-hover:bg-gradient-to-r group-hover:from-brand-blue group-hover:to-cyan-400 group-hover:bg-clip-text group-hover:text-transparent transition-all uppercase">
                        {blog.title}
                      </h3>
                      <p className="text-gray-500 text-sm">{blog.excerpt}</p>
                    </div>
                  </div>
                </Link>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* ============== NEWSLETTER SECTION (DARK) ============== */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden" data-testid="newsletter-section">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-brand-blue/10 via-cyan-500/10 to-purple-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <RevealOnScroll>
            <div className="w-16 h-16 bg-gradient-to-br from-brand-blue/20 to-cyan-400/20 rounded-2xl flex items-center justify-center mx-auto mb-8 border border-brand-blue/30">
              <Mail className="text-brand-blue w-8 h-8" />
            </div>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-4 uppercase">
              STAY AHEAD OF THE CURVE
            </h2>
            <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
              Get marketing insights, industry trends, and growth strategies delivered to your inbox.
            </p>
          </RevealOnScroll>
          
          <RevealOnScroll delay={0.2}>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 justify-center max-w-xl mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                data-testid="newsletter-input"
                className="flex-1 px-6 py-4 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-brand-blue transition-colors"
              />
              <button
                type="submit"
                data-testid="newsletter-btn"
                className="px-8 py-4 rounded-full bg-gradient-to-r from-brand-blue to-cyan-400 text-white font-semibold hover:from-cyan-400 hover:to-brand-blue transition-all duration-300 uppercase tracking-wide shadow-lg shadow-brand-blue/30"
              >
                SUBSCRIBE
              </button>
            </form>
            <p className="text-white/40 text-sm mt-4">No spam. Unsubscribe anytime.</p>
          </RevealOnScroll>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
